import type { CampaignEditorReducerCreator } from '../types';
import type { RootState } from '@/store/main';
import {
  selectCampaignEditorContent,
  selectCampaignEditorCurrentTemplateId,
  selectCampaignEditorState,
} from '../selectors';
import {
  campaignBlockLanguages,
  mergeBlocksForTemplateSwitch,
} from '@/micros/edit-project/grid-areas/main/campaigns/templates/merge-template-blocks';
import type { LanguagesAbbr } from '@/types/translation.types';
import { serializeBlocksForLanguage } from '@/utils/email-blocks';
import { loadEmailTranslations } from '@/utils/email-translations';
import { patchScheduledEmailCustomizations } from '../../scheduled-emails';
import { eventTitle as getEventTitle } from '@/micros/edit-project/grid-areas/main/campaigns/templates/utils/utils';

const selectCampaignTemplate = (create: CampaignEditorReducerCreator) =>
  create.asyncThunk(async (nextTemplateId: string, { getState, dispatch }) => {
    const state = getState() as RootState;
    const editor = selectCampaignEditorState(state);
    const currentTemplateId = selectCampaignEditorCurrentTemplateId(state);
    const { campaignUuid, blocks, baseLanguage } = editor;
    const language = editor.language ?? undefined;
    if (!campaignUuid || nextTemplateId === currentTemplateId) return;

    // the editor's own blocks are the current ones: the debounced sync runs 750ms behind, so the
    // store is still missing the edit the user made right before reaching for the strip - reading
    // it from there would read that edit as untouched template copy and reseed it away
    const stored = selectCampaignEditorContent(state)?.blocks;
    const previousBlocks = blocks ? serializeBlocksForLanguage(blocks, stored, language, baseLanguage) : stored;
    const languages = campaignBlockLanguages(previousBlocks, baseLanguage, language);
    const event = state.project.workingProject;
    const translations = await loadEmailTranslations(languages);

    dispatch(
      patchScheduledEmailCustomizations({
        uuid: campaignUuid,
        fields: {
          templateId: nextTemplateId,
          blocks: mergeBlocksForTemplateSwitch({
            nextTemplateId,
            previousTemplateId: currentTemplateId,
            previousBlocks,
            languages,
            baseLanguage,
            translations,
            eventTitleFor: (lang) => (event ? getEventTitle(event, lang as LanguagesAbbr) || event.name : ''),
          }),
        },
      })
    );
  });

export default selectCampaignTemplate;
